import React, { useEffect } from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dialog, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PrimaryButton from '../../../../../Generic/Buttons/PrimaryButton';
import SecondaryButton from '../../../../../Generic/Buttons/SecondaryButton';
import CreateGalleryHeader from './CreateGalleryHeader';
import Slide from '@mui/material/Slide';
import TitleAndDescription from './TitleAndDescription';
import UploadImages from './UploadImages';
import { useAppData } from '../../../../../../hooks/AppDataContext';
import PublishGallery from './PublishGallery';
import {
	createGallery,
	updateGallery,
	zipGallery
} from '../../../../../../services/galleryService';
import { ReactComponent as BackArrow } from '../../../../../../resources/svg/back-arrow.svg';
import { useModal } from '../../../../../../hooks/ModalContext';
import { deleteGallery } from '../../../../../../services/galleryService';
import { getGalleryImages } from '../../../../../../services/imageService';

const Root = styled(Dialog)(({ theme }) => ({ width: '100%' }));

const Paper = styled(Grid)(({ theme }) => ({
	width: '100%',
	backgroundColor: theme.palette.background.secondary,
	display: 'flex',
	flexDirection: 'column'
}));

const Header = styled(Grid)(({ theme }) => ({
	width: '100%',
	padding: '20px 30px',
	maxHeight: 'calc(100dvh - 63.5px)',
	display: 'flex',
	justifyContent: 'space-between'
}));

const DeleteButton = styled(Typography)(({ theme }) => ({
	fontSize: '0.8rem',
	color: theme.palette.error.main,
	cursor: 'pointer',
	userSelect: 'none'
}));

const CloseButton = styled(Typography)(({ theme }) => ({
	fontSize: '0.8rem',
	cursor: 'pointer',
	userSelect: 'none',
	display: 'flex',
	alignItems: 'center',
	gap: '7px'
}));

const BackIcon = styled(BackArrow)(({ theme }) => ({
	stroke: theme.palette.text.primary,
	width: '12px',
	height: '12px',
	strokeWidth: '1.5'
}));

const Body = styled(Grid)(({ theme }) => ({
	width: '100%',
	backgroundColor: '#FFF',
	borderRadius: '40px 40px 0 0',
	padding: '40px 60px 0px',
	maxHeight: 'calc(100dvh - 123.5px)',
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	overflow: 'auto'
}));

const ContentContainer = styled(Grid)(({ theme }) => ({
	width: '100%',
	padding: '40px 0px',
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center'
}));

const ActionBar = styled(Grid)(({ theme }) => ({
	width: '100%',
	padding: '12px 15px',
	position: 'fixed',
	bottom: 0,
	backgroundColor: '#FFF',
	borderTop: `solid 1px ${theme.palette.separator.primary}`,
	display: 'flex',
	justifyContent: 'space-between',
	zIndex: 300
}));

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const componentStack = [TitleAndDescription, UploadImages, PublishGallery];

function CreateGallery({ open, onClose }) {
	const [searchParams, setSearchParams] = useSearchParams();
	const [stackIndex, setStackIndex] = useState(0);
	const [data, setData] = useState({});
	const [errs, setErrs] = useState({});

	const galleryID = searchParams.get('create');
	const Content = componentStack[stackIndex];

	const { openModal } = useModal();
	const { galleries, setGalleries, updateGalleryState, deleteGalleryState } = useAppData();

	const handleSetData = (d) => {
		setData(d);
		setErrs({});
	};

	/**
	 * Validate that the fields are populated.
	 */
	const validateFields = () => {
		let errors = {};

		if (!data?.title) {
			errors.title = 'Please enter a name for your gallery';
		}

		if (!data?.description) {
			errors.description = 'Please enter a description for your gallery';
		}

		setErrs(errors);
		return errors;
	};

	const handleDeleteGallery = async () => {
		await deleteGallery(galleryID);
		deleteGalleryState(galleryID);
		handleClose();
	};

	const onDelete = () => {
		if (galleryID === 'new') {
			handleClose();
		} else {
			const modalData = {
				title: 'Confirmation Required',
				message: `Are you sure you want to delete this gallery?\nThis action cannot be undone.`,
				onConfirm: handleDeleteGallery
			};

			openModal(modalData);
		}
	};

	const isDirty = () => {
		const gallery = galleries.data.find((g) => g._id === galleryID);
		const fields = ['title', 'description'];
		return fields.some((key) => gallery?.[key] !== data?.[key]);
	};

	const saveTitleAndDescription = async () => {
		const errors = validateFields();
		if (!!Object.keys(errors).length) throw 'Invalid inputs';
		if (galleryID === 'new') {
			const response = await createGallery(data?.title, data?.description);
			setSearchParams({ create: response });
			const DAY = 24 * 60 * 60 * 1000;
			const gallery = {
				_id: response,
				title: data?.title,
				status: 'draft',
				description: data?.description,
				duration: 30 * DAY
			};
			const updatedGalleriesData = [...galleries.data, gallery];
			setGalleries({ ...galleries, data: updatedGalleriesData });
			setData({ ...data, duration: gallery?.duration, _id: gallery?._id });
		} else if (isDirty()) {
			const updatedData = { title: data?.title, description: data?.description };
			await updateGallery(galleryID, updatedData);
			updateGalleryState(updatedData);
		}
	};

	/**
	 * Publish the gallery
	 */
	const publishGallery = async () => {
		const gallery = galleries.data.find((g) => g._id === galleryID);

		const updatedFields = {
			duration: gallery?.duration,
			expiration: Date.now() + gallery?.duration,
			thumbnail: gallery?.thumbnail,
			status: 'published'
		};

		const promises = [
			updateGallery(galleryID, updatedFields),
			zipGallery(galleryID, gallery?.title)
		];

		await Promise.all(promises);
		updateGalleryState({ ...gallery, ...updatedFields });

		const modalData = {
			title: 'Congratulations',
			message: `Your gallery is now live! You can now copy the link and share it with your client.`
		};

		openModal(modalData);

		handleClose();
		setStackIndex(0);
	};

	const handleNext = async () => {
		try {
			if (stackIndex === 0) {
				await saveTitleAndDescription();
				setStackIndex(stackIndex + 1);
			}
			if (stackIndex === 1) {
				console.log('data?.images: ', data?.images);
				setStackIndex(stackIndex + 1);
				const updatedData = {
					...data,
					thumbnail: data?.thumbnail || data?.images?.[0]?.optimized
				};
				setData(updatedData);
				updateGalleryState(updatedData);
			}
			if (stackIndex === 2) {
				await publishGallery();
			}
		} catch (e) {
			console.error(e);
		}
	};

	const handlePrevious = () => {
		if (stackIndex > 0) {
			setStackIndex(stackIndex - 1);
		}
	};

	const handleClose = () => {
		setSearchParams({});
		setData({});
		setStackIndex(0);
		setErrs({});
	};

	const fetchGalleryImages = async () => {
		if (galleryID && galleryID !== 'new' && !data?.images) {
			const { images } = await getGalleryImages(galleryID);
			setData((prev) => ({ ...prev, images }));
		}
	};

	useEffect(() => {
		console.log('The new data: ', data);
	}, [data]);

	useEffect(() => {
		const gallery = galleries.data.find((g) => g._id === galleryID);
		console.log('The new gallery: ', gallery);
	}, [galleries]);

	useEffect(() => {
		if (!!galleries?.loadedAt && !!galleryID && galleryID !== 'new' && stackIndex === 0) {
			const gallery = galleries.data.find((g) => g._id === galleryID);
			handleSetData({ ...data, ...gallery });
		}
		fetchGalleryImages();
	}, [galleryID, galleries?.loadedAt]);

	return (
		<Root fullScreen open={open} TransitionComponent={Transition}>
			<Paper>
				<Header>
					<CloseButton onClick={handleClose}>
						<BackIcon />
						Back to Galleries
					</CloseButton>
					<DeleteButton onClick={onDelete}>Delete Draft</DeleteButton>
				</Header>
				<Body>
					<CreateGalleryHeader index={stackIndex} />
					<ContentContainer>
						<Content data={data} setData={handleSetData} errs={errs} />
					</ContentContainer>
				</Body>
				<ActionBar>
					<SecondaryButton
						sx={{ visibility: stackIndex === 0 ? 'hidden' : 'visible' }}
						onClick={handlePrevious}
					>
						Back
					</SecondaryButton>
					<PrimaryButton onClick={handleNext}>
						{stackIndex === 2 ? 'Publish' : 'Continue'}
					</PrimaryButton>
				</ActionBar>
			</Paper>
		</Root>
	);
}

export default CreateGallery;
